// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {gsap} from 'gsap'
import {SplitText} from 'gsap/SplitText'
import {useEffect, useRef, useState} from 'react'

import type {MonaHeadHandle} from '../MonaHead/MonaHead'
import {createTitleFragments, type IntroTitle} from './utils/intro-title'

interface MotionOptions {
  prefersReducedMotion: boolean
  title: IntroTitle
}

gsap.registerPlugin(SplitText)

export const useMotion = ({prefersReducedMotion, title}: MotionOptions) => {
  const [isReady, setIsReady] = useState(false)
  const [hasTitleSplitFailed, setHasTitleSplitFailed] = useState(false)
  const hasTitleAnimationFailed = hasTitleSplitFailed || title.parts === null
  const [hasAnimatedIn, setHasAnimatedIn] = useState<boolean>(false)
  if ((prefersReducedMotion || hasTitleAnimationFailed) && !hasAnimatedIn) setHasAnimatedIn(true)

  const tl = useRef<gsap.core.Timeline>(null)
  const titleTl = useRef<gsap.core.Timeline>(null)
  const splitText = useRef<SplitText | null>(null)

  const contentRef = useRef<HTMLDivElement>(null)
  const eyebrowBannerRef = useRef<HTMLDivElement>(null)
  const monaRef = useRef<HTMLDivElement>(null)
  const monaHeadRef = useRef<MonaHeadHandle>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)

  const hasRequestedMonaEntranceRef = useRef(false)
  const hasSkippedEntranceRef = useRef(false)

  const enterMona = () => monaHeadRef.current?.enterMona()

  const showTitle = () => {
    titleTl.current?.kill()
    // Preparation and playback create separate from() tweens on the same elements.
    // Defer their starting styles so playback reads the original styles as its destinations.
    titleTl.current = gsap.timeline({defaults: {immediateRender: false}})

    if (!splitText.current) {
      return titleTl.current
    }

    const lines = splitText.current.lines
    const firstLine = lines[0]
    if (!firstLine) return titleTl.current

    const words = lines.flatMap(line => Array.from(line.querySelectorAll<HTMLElement>('.word')))
    const groups = new Map<string | number, HTMLElement[]>()
    for (const [index, word] of words.entries()) {
      const id = word.getAttribute('data-reveal-group') ?? index
      const group = groups.get(id)
      if (group) group.push(word)
      else groups.set(id, [word])
    }

    const [openingGroup, ...restGroups] = [...groups.values()]
    if (!openingGroup) return titleTl.current

    const markedWords = openingGroup.filter(word => word.hasAttribute('data-impact'))
    const impactTargets = markedWords.length ? markedWords : openingGroup
    const openingLines = lines.filter(line => openingGroup.some(word => line.contains(word)))
    const firstLineGroups = restGroups.filter(group => group.some(word => firstLine.contains(word)))
    const remainingGroups = restGroups.filter(group => !firstLineGroups.includes(group))

    const wordDuration = 0.3
    const wordStagger = 0.15
    const colorGreen = 'var(--color-greenEmphasis)'
    const ease = 'expo.out'

    titleTl.current.set(words, {opacity: 0})

    titleTl.current.addLabel('firstWord')
    titleTl.current.set(openingGroup, {opacity: ''})
    titleTl.current.set(restGroups.flat(), {color: colorGreen})
    titleTl.current.fromTo(openingLines, {y: '0.75em'}, {y: '0.25em', duration: wordDuration, ease})
    titleTl.current.from(impactTargets, {color: colorGreen, duration: wordDuration, ease}, 'firstWord')

    titleTl.current.addLabel('firstLineRest')
    titleTl.current.to(openingLines, {y: 0})
    titleTl.current.set(firstLineGroups.flat(), {opacity: '', y: '0.25em'}, 'firstLineRest')
    for (const [index, group] of firstLineGroups.entries()) {
      titleTl.current.to(group, {y: 0, duration: wordDuration, ease}, `firstLineRest+=${index * wordStagger}`)
    }

    titleTl.current.addLabel('remainingLines')
    for (const [index, group] of remainingGroups.entries()) {
      const position = `remainingLines+=${index * wordStagger}`
      titleTl.current.set(group, {opacity: ''}, position)
      titleTl.current.from(group, {x: '0.5ch', duration: wordDuration, ease}, position)
    }

    titleTl.current.set(words, {color: ''})
    return titleTl.current
  }

  const show = (delay = 0) => {
    // SplitText tracks this instance while preparation and playback populate it.
    tl.current ??= gsap.timeline()
    tl.current.clear()

    if (prefersReducedMotion || hasTitleAnimationFailed || hasSkippedEntranceRef.current) {
      setHasAnimatedIn(true)
      return tl
    }
    const monaParticles = monaRef.current?.querySelector('svg')
    if (!splitText.current || !monaParticles) return tl

    tl.current.eventCallback('onComplete', () => setHasAnimatedIn(true))

    tl.current.addLabel('start', delay)
    tl.current.call(
      () => {
        monaParticles.setAttribute('data-paused', '')
        gsap.set([contentRef.current, monaRef.current], {opacity: ''})
      },
      [],
      'start',
    )

    tl.current.addLabel('title')
    tl.current.add(showTitle())

    tl.current.addLabel('mona', '-=0.6')
    tl.current.call(
      () => {
        if (hasRequestedMonaEntranceRef.current) return
        hasRequestedMonaEntranceRef.current = true
        enterMona()
      },
      [],
      'mona+=0.1',
    )
    tl.current.fromTo(
      monaParticles,
      {opacity: 0},
      {
        opacity: 1,
        duration: 0.2,
        clearProps: 'opacity',
        onStart: () => {
          monaParticles.removeAttribute('data-paused')
        },
      },
      '+=0.1',
    )

    if (eyebrowBannerRef.current) {
      tl.current.addLabel('eyebrowBanner', 'title+=0.4')
      tl.current.fromTo(
        eyebrowBannerRef.current,
        {opacity: 0},
        {
          opacity: 1,
          duration: 0.4,
          clearProps: 'opacity',
        },
        'eyebrowBanner',
      )
    }

    tl.current.play(0)
    return tl
  }

  useEffect(() => {
    if (!prefersReducedMotion && !hasTitleAnimationFailed) return

    hasSkippedEntranceRef.current = true
    tl.current?.revert()
    splitText.current?.revert()
    splitText.current = null

    const monaParticles = monaRef.current?.querySelector('svg')
    monaParticles?.removeAttribute('data-paused')
    for (const element of [contentRef.current, monaRef.current, eyebrowBannerRef.current, monaParticles]) {
      element?.style.removeProperty('opacity')
    }

    if (hasTitleAnimationFailed && !prefersReducedMotion && !hasRequestedMonaEntranceRef.current) {
      hasRequestedMonaEntranceRef.current = true
      monaHeadRef.current?.enterMona()
    }
  }, [prefersReducedMotion, hasTitleAnimationFailed])

  const prepare = () => {
    let cancelled = false

    const setup = async (el: HTMLElement | null) => {
      const {parts} = title
      if (!el || splitText.current || parts === null) return

      await document.fonts.ready
      if (cancelled || hasSkippedEntranceRef.current || !el.isConnected || splitText.current) return

      SplitText.create(el, {
        type: parts.length ? 'chars,lines' : 'words,lines',
        ...(parts.length
          ? {tag: 'span', wordDelimiter: '', reduceWhiteSpace: false, prepareText: (text: string) => text.trim()}
          : {}),
        linesClass: 'line',
        wordsClass: 'word',
        autoSplit: true,
        onSplit: self => {
          splitText.current = self
          if (parts.length) {
            // SplitText's textContent-based label omits native line breaks.
            el.setAttribute(
              'aria-label',
              title.lines
                .map(line => line.text)
                .join(' ')
                .trim(),
            )
            gsap.set(self.lines, {display: 'block'})
            // Returns false if SplitText's text does not match the authored text, so skip the animation.
            if (!createTitleFragments(self, parts, title.lines)) {
              hasSkippedEntranceRef.current = true
              // Revert in the effect, after SplitText finishes setting up its resize observer.
              setHasTitleSplitFailed(true)
              if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console -- Authoring feedback only; do not report cosmetic failures in production.
                console.warn('Homepage title split does not match its authored text; skipping animation.')
              }
              return
            }
          }

          for (const line of self.lines) {
            line.setAttribute('data-content', line.textContent || '')
          }

          return show(tl.current?.labels.start).current
        },
      })

      if (!hasSkippedEntranceRef.current) {
        tl.current?.pause(0)
        setIsReady(true)
      }
    }

    if (splitText.current) {
      splitText.current.revert()
      splitText.current = null
    }

    setup(headingRef.current)

    return () => {
      cancelled = true
      hasRequestedMonaEntranceRef.current = false
      tl.current?.kill()
      tl.current = null
      splitText.current?.revert()
      splitText.current = null
    }
  }

  return {
    contentRef,
    eyebrowBannerRef,
    monaRef,
    monaHeadRef,
    headingRef,
    isReady,
    hasAnimatedIn,
    prepare,
    show,
    enterMona,
  }
}
